import { FunctionComponent, useState } from "react";

import { classNames } from "@/utils/classNames";

export type CompanyLogoAvatarSize = "xs" | "sm" | "md";

export interface CompanyLogoAvatarProps {
	logoUrl: string | null;
	name: string;
	size?: CompanyLogoAvatarSize;
	className?: string;
}

const sizeClasses: Record<CompanyLogoAvatarSize, string> = {
	// Small enough to sit inline in a line of meta text next to the company name.
	xs: "h-3.5 w-3.5 text-[7px]",
	sm: "h-6 w-6 text-[10px]",
	md: "h-8 w-8 text-xs",
};

/** Company logo with initials fallback when the URL is missing or fails to load. */
export const CompanyLogoAvatar: FunctionComponent<CompanyLogoAvatarProps> = ({
	logoUrl,
	name,
	size = "md",
	className,
}) => {
	// Track the URL that failed (rather than a boolean) so the error state resets
	// automatically when a different logo is rendered in the same component instance.
	const [failedUrl, setFailedUrl] = useState<string | null>(null);
	const initials = name?.slice(0, 2)?.toUpperCase() ?? "";

	if (logoUrl && failedUrl !== logoUrl) {
		return (
			<img
				alt={name}
				className={classNames(
					"flex-shrink-0 rounded-md object-contain",
					sizeClasses[size],
					className
				)}
				referrerPolicy="no-referrer"
				src={logoUrl}
				onError={() => setFailedUrl(logoUrl)}
			/>
		);
	}

	return (
		<div
			className={classNames(
				"flex flex-shrink-0 items-center justify-center rounded-md bg-gray-100 font-medium text-text-meta",
				sizeClasses[size],
				className
			)}
		>
			{initials}
		</div>
	);
};
